/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import {
  ResponsiveTable,
  RightSheet,
  type ResponsiveTableColumn,
  useLocalization,
} from "@lemmonhq/uikit";
import type { GroupIdentityListItem } from "@app/server/trpc/routers/identities";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";
import { useGroupAuth } from "@app/vue/providers/GroupAuth";
import { useToast } from "@app/vue/providers/Toast";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { IdentityForm, type IdentityFormValue } from "./IdentityForm";

const PAGE_SIZE = 20;

export const GroupUsersPage = defineComponent({
  name: "GroupUsersPage",
  setup() {
    const { t } = useLocalization();
    const auth = useGroupAuth();
    const trpc = useTrpc();
    const toast = useToast();
    const queryClient = useQueryClient();
    const searchDraft = ref("");
    const search = ref("");
    const page = ref(0);
    const sheetOpen = ref(false);
    const formMode = ref<"create" | "edit">("create");
    const selectedIdentity = ref<GroupIdentityListItem | null>(null);
    const saving = ref(false);

    const identitiesQuery = useQuery({
      queryKey: computed(() => [
        "group",
        "identities",
        search.value,
        page.value,
      ]),
      enabled: auth.isGroupAdmin,
      queryFn: () =>
        trpc.identities.list.query({
          search: search.value,
          limit: PAGE_SIZE,
          offset: page.value * PAGE_SIZE,
        }),
    });

    const shopsQuery = useQuery({
      queryKey: ["group", "shops"],
      enabled: auth.isGroupAdmin,
      queryFn: () => trpc.shops.list.query(),
    });

    const refreshGroupData = async () => {
      await queryClient.invalidateQueries({ queryKey: ["group"] });
    };

    const closeSheet = () => {
      if (saving.value) return;
      sheetOpen.value = false;
      selectedIdentity.value = null;
    };

    const submitForm = async (value: IdentityFormValue) => {
      saving.value = true;
      try {
        if (formMode.value === "create") {
          await trpc.identities.create.mutate({
            email: value.email,
            displayName: value.displayName,
            groupRole: value.groupRole,
            grants: value.grants,
            password: value.password!,
          });
          toast.showToast(t("admin.users.create_success"), { type: "success" });
        } else if (selectedIdentity.value) {
          await trpc.identities.updateGroupRole.mutate({
            identityId: selectedIdentity.value.id,
            role: value.groupRole,
          });
          await trpc.identities.updateGrants.mutate({
            identityId: selectedIdentity.value.id,
            displayName: value.displayName,
            // The grants primitive is role-agnostic and would otherwise
            // remove the manager personas just reconciled for an admin.
            grants:
              value.groupRole === "admin"
                ? (shopsQuery.data.value ?? []).map((shop) => ({
                    restaurantId: shop.id,
                    role: "manager" as const,
                  }))
                : value.grants,
          });
          toast.showToast(t("admin.users.update_success"), { type: "success" });
        }

        await refreshGroupData();
        sheetOpen.value = false;
        selectedIdentity.value = null;
      } catch (error) {
        const message =
          error instanceof Error && /final group admin/i.test(error.message)
            ? t("admin.users.last_admin_error")
            : t("admin.users.save_error");
        toast.showToast(message, { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    const removeIdentity = async () => {
      if (!selectedIdentity.value) return;
      saving.value = true;
      try {
        await trpc.identities.removeFromGroup.mutate({
          identityId: selectedIdentity.value.id,
        });
        await refreshGroupData();
        sheetOpen.value = false;
        selectedIdentity.value = null;
        toast.showToast(t("admin.users.remove_success"), { type: "success" });
      } catch (error) {
        const message =
          error instanceof Error && /final group admin/i.test(error.message)
            ? t("admin.users.last_admin_error")
            : t("admin.users.remove_error");
        toast.showToast(message, { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    const columns: ResponsiveTableColumn[] = [
      {
        key: "identity",
        label: t("admin.users.identity"),
        icon: "person",
        mobilePriority: "primary",
        render: (identity: GroupIdentityListItem) => (
          <div class="min-w-0">
            <div class="truncate text-accent">{identity.name}</div>
            <div class="truncate text-xs text-base-content/50">
              {identity.email}
            </div>
          </div>
        ),
      },
      {
        key: "status",
        label: t("admin.users.status"),
        mobilePriority: "secondary",
        render: (identity: GroupIdentityListItem) =>
          t(`admin.status.${identity.status}`),
      },
      {
        key: "group-role",
        label: t("admin.users.group_role"),
        mobilePriority: "secondary",
        render: (identity: GroupIdentityListItem) => (
          <span class="badge badge-outline badge-sm">
            {t(`admin.roles.group_${identity.group_role}`)}
          </span>
        ),
      },
      {
        key: "shops",
        label: t("admin.users.linked_shops"),
        mobilePriority: "secondary",
        render: (identity: GroupIdentityListItem) =>
          identity.grants.length ? (
            <div class="flex flex-wrap gap-1 max-w-[32rem]">
              {identity.grants.map((grant) => (
                <span
                  key={grant.restaurant_id}
                  class="badge badge-ghost badge-sm gap-1"
                >
                  {grant.restaurant_name}
                  <span class="text-base-content/50">
                    ·{" "}
                    {grant.pos_role
                      ? t(`admin.roles.pos_${grant.pos_role}`)
                      : t("admin.common.not_available")}
                  </span>
                </span>
              ))}
            </div>
          ) : (
            <span class="text-base-content/50">
              {t("admin.users.no_shop_access")}
            </span>
          ),
      },
      {
        key: "actions",
        label: t("admin.common.actions"),
        mobilePriority: "actions",
        render: () => (
          <button class="btn btn-ghost btn-sm" type="button">
            <span class="material-symbols-outlined text-lg">edit_square</span>
          </button>
        ),
      },
    ];

    const pagination = computed(() => identitiesQuery.data.value?.pagination);
    const hasPrevious = computed(() => page.value > 0);
    const hasNext = computed(
      () => (page.value + 1) * PAGE_SIZE < (pagination.value?.total ?? 0),
    );

    return () => (
      <div class="flex flex-1 flex-col">
        <AppSubpageHeader
          icon="group"
          title={t("admin.users.title")}
          description={auth.group.value.name}
        >
          {{
            actions: () => (
              <button
                class="btn btn-primary btn-sm gap-2"
                type="button"
                onClick={() => {
                  formMode.value = "create";
                  selectedIdentity.value = null;
                  sheetOpen.value = true;
                }}
              >
                <span class="material-symbols-outlined text-lg">person_add</span>
                {t("admin.users.add_user")}
              </button>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent>
            <div class="mx-auto w-full max-w-6xl space-y-6">
              <form
                class="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            page.value = 0;
            search.value = searchDraft.value.trim();
          }}
        >
          <label class="input input-bordered flex flex-1 items-center gap-2">
            <span class="material-symbols-outlined text-base-content/50">
              search
            </span>
            <input
              class="grow"
              type="search"
              placeholder={t("admin.users.search_placeholder")}
              value={searchDraft.value}
              onInput={(event) => {
                searchDraft.value = (event.target as HTMLInputElement).value;
              }}
            />
          </label>
          <button class="btn btn-neutral" type="submit">
            {t("admin.common.search")}
          </button>
        </form>

        {identitiesQuery.isError.value ? (
          <div class="alert alert-error">
            <span class="material-symbols-outlined">error</span>
            <span>{t("admin.users.load_error")}</span>
            <button
              class="btn btn-sm"
              type="button"
              onClick={() => void identitiesQuery.refetch()}
            >
              {t("admin.common.retry")}
            </button>
          </div>
        ) : (
          <div class="overflow-hidden rounded-xl border border-base-content/8 bg-base-100">
            <ResponsiveTable
              columns={columns}
              rows={identitiesQuery.data.value?.data ?? []}
              rowKey={(identity: GroupIdentityListItem) => identity.id}
              onRowClick={(identity: GroupIdentityListItem) => {
                formMode.value = "edit";
                selectedIdentity.value = identity;
                sheetOpen.value = true;
              }}
              loading={identitiesQuery.isPending.value}
              emptyMessage={t("admin.users.empty")}
              stickyHeader={false}
            />
          </div>
        )}

        {(pagination.value?.total ?? 0) > PAGE_SIZE && (
          <div class="flex items-center justify-between">
            <span class="text-sm text-base-content/60">
              {t("admin.users.pagination")
                .replace("{from}", String(page.value * PAGE_SIZE + 1))
                .replace(
                  "{to}",
                  String(
                    Math.min(
                      (page.value + 1) * PAGE_SIZE,
                      pagination.value?.total ?? 0,
                    ),
                  ),
                )
                .replace("{total}", String(pagination.value?.total ?? 0))}
            </span>
            <div class="join">
              <button
                class="btn join-item btn-sm"
                type="button"
                disabled={!hasPrevious.value}
                onClick={() => {
                  page.value -= 1;
                }}
              >
                {t("admin.common.previous")}
              </button>
              <button
                class="btn join-item btn-sm"
                type="button"
                disabled={!hasNext.value}
                onClick={() => {
                  page.value += 1;
                }}
              >
                {t("admin.common.next")}
              </button>
            </div>
          </div>
        )}

        {sheetOpen.value && (
          <RightSheet
            isOpen={sheetOpen.value}
            title={
              formMode.value === "create"
                ? t("admin.users.add_user")
                : t("admin.users.edit_user")
            }
            width="600px"
            onClose={closeSheet}
          >
            <IdentityForm
              mode={formMode.value}
              identity={selectedIdentity.value}
              shops={shopsQuery.data.value ?? []}
              busy={saving.value || shopsQuery.isPending.value}
              onSubmit={submitForm}
              onCancel={closeSheet}
              onRemove={formMode.value === "edit" ? removeIdentity : undefined}
            />
          </RightSheet>
        )}
            </div>
          </AppSubpageContent>
        </AppSubpage>
      </div>
    );
  },
});

export * from "./IdentityForm";
